import type { HTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertTriangle, CheckCircle2, Info, type LucideIcon, X, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

import { IconButton } from './IconButton';

/**
 * Alert — komunikat inline (Figma „08.7 · Feedback / Alert").
 *
 * Dwie osie: `tone` (info/success/warning/error) × `variant` (subtle/outline/solid).
 *  - subtle  — tło `*-subtle` + tekst `*-text` + ramka `*-border`, ikona w kolorze base.
 *  - outline — przezroczyste tło + ramka base, tekst `*-text`, ikona base.
 *  - solid   — tło base, tekst i ikona białe.
 *
 * Znaczenie zawsze niosą IKONA + TEKST (nie sam kolor). Ikona statusu 20px, tytuł
 * Body/Default Medium (14/500), treść Body/Small (13px). Opcjonalne `actions` (slot pod
 * treścią) i `onDismiss` (przycisk „X"). Tony jadą na STAŁYCH tokenach semantycznych
 * (patrz index.css) — czytelne niezależnie od motywu akcentu tenanta.
 */

export type AlertTone = 'info' | 'success' | 'warning' | 'error';
export type AlertVariant = 'subtle' | 'outline' | 'solid';

const alertVariants = cva('flex items-start gap-3 rounded-lg border p-3', {
  variants: {
    tone: { info: '', success: '', warning: '', error: '' },
    variant: {
      subtle: '',
      outline: 'bg-transparent',
      solid: 'border-transparent text-white',
    },
  },
  compoundVariants: [
    // — subtle —
    {
      tone: 'info',
      variant: 'subtle',
      class:
        'bg-[rgb(var(--color-info-subtle))] border-[rgb(var(--color-info-border))] text-[rgb(var(--color-info-text))]',
    },
    {
      tone: 'success',
      variant: 'subtle',
      class:
        'bg-[rgb(var(--color-success-subtle))] border-[rgb(var(--color-success-border))] text-[rgb(var(--color-success-text))]',
    },
    {
      tone: 'warning',
      variant: 'subtle',
      class:
        'bg-[rgb(var(--color-warning-subtle))] border-[rgb(var(--color-warning-border))] text-[rgb(var(--color-warning-text))]',
    },
    {
      tone: 'error',
      variant: 'subtle',
      class:
        'bg-[rgb(var(--color-error-subtle))] border-[rgb(var(--color-error-border))] text-[rgb(var(--color-error-text))]',
    },
    // — outline —
    {
      tone: 'info',
      variant: 'outline',
      class: 'border-[rgb(var(--color-info))] text-[rgb(var(--color-info-text))]',
    },
    {
      tone: 'success',
      variant: 'outline',
      class: 'border-[rgb(var(--color-success))] text-[rgb(var(--color-success-text))]',
    },
    {
      tone: 'warning',
      variant: 'outline',
      class: 'border-[rgb(var(--color-warning))] text-[rgb(var(--color-warning-text))]',
    },
    {
      tone: 'error',
      variant: 'outline',
      class: 'border-[rgb(var(--color-error))] text-[rgb(var(--color-error-text))]',
    },
    // — solid (tekst biały z bazowego wariantu) —
    { tone: 'info', variant: 'solid', class: 'bg-[rgb(var(--color-info))]' },
    { tone: 'success', variant: 'solid', class: 'bg-[rgb(var(--color-success))]' },
    { tone: 'warning', variant: 'solid', class: 'bg-[rgb(var(--color-warning))]' },
    { tone: 'error', variant: 'solid', class: 'bg-[rgb(var(--color-error))]' },
  ],
  defaultVariants: { tone: 'info', variant: 'subtle' },
});

const TONE_ICON: Record<AlertTone, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
};

// Kolor ikony: dla solid biały (dziedziczy z tekstu), inaczej base tonu.
const TONE_ICON_COLOR: Record<AlertTone, string> = {
  info: 'text-[rgb(var(--color-info))]',
  success: 'text-[rgb(var(--color-success))]',
  warning: 'text-[rgb(var(--color-warning))]',
  error: 'text-[rgb(var(--color-error))]',
};

export interface AlertProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'>, VariantProps<typeof alertVariants> {
  tone?: AlertTone;
  variant?: AlertVariant;
  /** Tytuł (Body/Default Medium). Opcjonalny — sam `children` też wystarczy. */
  title?: ReactNode;
  /** Treść komunikatu (Body/Small). */
  children?: ReactNode;
  /** Nadpisanie ikony statusu (domyślnie wg tonu). */
  icon?: LucideIcon;
  /** Slot akcji pod treścią (np. przyciski). */
  actions?: ReactNode;
  /** Gdy podane — renderuje przycisk zamknięcia. */
  onDismiss?: () => void;
  /** Etykieta a11y przycisku zamknięcia. */
  dismissLabel?: string;
}

export function Alert({
  tone = 'info',
  variant = 'subtle',
  title,
  children,
  icon,
  actions,
  onDismiss,
  dismissLabel = 'Zamknij',
  className,
  ...rest
}: AlertProps) {
  const IconComponent = icon ?? TONE_ICON[tone];
  const isSolid = variant === 'solid';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(alertVariants({ tone, variant }), className)}
      {...rest}
    >
      <IconComponent
        size={20}
        aria-hidden="true"
        className={cn('mt-0.5 shrink-0', isSolid ? 'text-white' : TONE_ICON_COLOR[tone])}
      />
      <div className="min-w-0 flex-1">
        {title && <div className="text-sm font-medium">{title}</div>}
        {children && (
          <div className={cn('text-[13px] leading-5 whitespace-pre-wrap', title && 'mt-0.5')}>
            {children}
          </div>
        )}
        {actions && <div className="mt-2 flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {onDismiss && (
        <IconButton
          variant="ghost"
          size="sm"
          aria-label={dismissLabel}
          onClick={onDismiss}
          className={cn(
            '-mr-1 -mt-1 shrink-0',
            isSolid && 'text-white hover:bg-white/20 hover:text-white',
          )}
        >
          <X />
        </IconButton>
      )}
    </div>
  );
}
